import styled from 'styled-components';
import { colors, breakpoints, maxBreakpoints } from '../../constants';
import {
  Button,
  Badge as BaseBadge,
  FormTitle as BaseFormTitle,
  FormLabel as BaseFormLabel,
  FormInput as BaseFormInput,
  ErrorText as BaseErrorText,
} from '../../constants/base';

// ==============================================
// LAYOUT COMPONENTS
// ==============================================

export const Container = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  background-color: ${colors.gray01};
  font-family: Mulish;
  min-height: 100vh;
  width: 100%;
  padding: 0;
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  button {
    margin-top: 16px;
  }
`;

// ==============================================
// BASE COMPONENTS (from constants/base)
// ==============================================

export const ErrorText = BaseErrorText;

export const Chip = styled(BaseBadge)`
  background-color: ${colors.lightred};
  color: ${colors.red};
`;

export const Title = BaseFormTitle;
export const InputLabel = BaseFormLabel;

export const FormInput = styled(BaseFormInput)`
  border-color: ${(props) =>
    props.error ? colors.error : colors.inputBorderGray};

  &:focus {
    border-color: ${(props) =>
      props.error ? colors.error : colors.inputFocusRed};
    box-shadow: ${(props) =>
      props.error
        ? `0 0 0 2px ${colors.errorAlpha}`
        : `0 0 0 2px ${colors.inputFocusRedAlpha}`};
  }
`;

export const HelperText = styled.span`
  font-size: 12px;
  color: ${(props) => (props.error ? colors.error : colors.gray05)};
  margin-top: 4px;
  display: block;
  font-family: Mulish;
`;

// ==============================================
// BOX COMPONENTS
// ==============================================

export const BoxBase = styled.div`
  width: 100%;
  height: auto;
  min-height: fit-content;
  margin: 20px 10px 100px 10px;
  background-color: ${colors.white};
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px 15px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  /* Mobile M (375px+) */
  @media ${breakpoints.mobileM} {
    margin: 30px 15px 120px 15px;
    padding: 35px 18px;
    border-radius: 7px;
  }

  /* Mobile L (425px+) */
  @media ${breakpoints.mobileL} {
    margin: 40px 18px 130px 18px;
    padding: 38px 20px;
    border-radius: 8px;
  }

  /* Tablet (768px+) */
  @media ${breakpoints.tablet} {
    margin: 50px 30px 140px 30px;
    padding: 45px 30px;
    margin-left: auto;
    margin-right: auto;
  }

  /* Laptop (1024px+) */
  @media ${breakpoints.laptop} {
    margin: 60px 40px 160px 40px;
    padding: 50px 40px;
  }

  /* Laptop M (1366px+) */
  @media ${breakpoints.laptopM} {
    margin: 70px 50px 180px 50px;
    padding: 55px 50px;
  }

  /* Laptop L (1440px+) */
  @media ${breakpoints.laptopL} {
    margin: 80px 60px 200px 60px;
    padding: 60px 60px;
  }

  /* Desktop (2560px+) */
  @media ${breakpoints.desktop} {
    margin: 100px auto 220px auto;
    padding: 70px 80px;
  }
`;

export const BoxRegisterForm = styled(BoxBase)`
  max-width: 650px;

  /* Centrar en pantallas grandes */
  @media ${breakpoints.tablet} {
    margin-left: auto;
    margin-right: auto;
  }
`;

export const BoxConfirmEmailAddress = styled(BoxBase)`
  max-width: 550px;

  /* Centrar en pantallas grandes */
  @media ${breakpoints.tablet} {
    margin-left: auto;
    margin-right: auto;
  }
`;

// ==============================================
// FORM LAYOUT COMPONENTS
// ==============================================

export const FormBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-top: 12px;
  width: 100%;
  gap: 12px;
  transition: all 0.3s ease;

  /* Mobile M (375px+) */
  @media ${breakpoints.mobileM} {
    margin-top: 14px;
    gap: 14px;
  }

  /* Mobile L (425px+) */
  @media ${breakpoints.mobileL} {
    margin-top: 16px;
    gap: 15px;
  }

  /* Tablet (768px+) - switch to row layout */
  @media ${breakpoints.tablet} {
    flex-direction: row;
    margin-top: 18px;
    gap: 20px;
  }

  /* Laptop (1024px+) */
  @media ${breakpoints.laptop} {
    margin-top: 20px;
    gap: 24px;
  }

  /* Laptop M (1366px+) */
  @media ${breakpoints.laptopM} {
    margin-top: 22px;
    gap: 28px;
  }
`;

export const InnerFormBox = styled.div`
  margin: 0 8px;
  width: 100%;
  transition: all 0.3s ease;

  /* Mobile adjustments */
  @media ${maxBreakpoints.mobileL} {
    margin: 0 4px;
  }

  @media ${breakpoints.mobileM} {
    margin: 0 6px;
  }

  @media ${breakpoints.mobileL} {
    margin: 0 7px;
  }

  /* Tablet and above */
  @media ${breakpoints.tablet} {
    margin: 0 8px;
  }

  @media ${breakpoints.laptop} {
    margin: 0 10px;
  }

  @media ${breakpoints.laptopM} {
    margin: 0 12px;
  }
`;

// ==============================================
// MATERIAL-UI WRAPPER COMPONENTS
// ==============================================

export const TextFieldWrap = styled.div`
  width: 100%;
  transition: all 0.3s ease;
`;

// ==============================================
// BUTTON COMPONENTS
// ==============================================

export const SignUpButton = styled(Button)`
  padding: 0;
  margin-top: 40px;
  font-family: Mulish;
  font-size: 16px;
  font-weight: 900;
  width: 250px;
  height: 50px;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${colors.red01};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(211, 47, 47, 0.3);
  }
  &:active {
    background-color: ${colors.red};
    transform: translateY(0);
  }

  /* Mobile M (375px+) */
  @media ${breakpoints.mobileM} {
    margin-top: 45px;
    font-size: 17px;
    width: 260px;
    height: 52px;
  }

  /* Mobile L (425px+) */
  @media ${breakpoints.mobileL} {
    margin-top: 50px;
    font-size: 17px;
    width: 270px;
    height: 54px;
  }

  /* Tablet (768px+) */
  @media ${breakpoints.tablet} {
    margin-top: 55px;
    font-size: 18px;
    width: 287px;
    height: 58px;
  }

  /* Laptop (1024px+) */
  @media ${breakpoints.laptop} {
    margin-top: 60px;
    font-size: 19px;
    width: 300px;
    height: 60px;
  }

  /* Laptop M (1366px+) */
  @media ${breakpoints.laptopM} {
    margin-top: 65px;
    font-size: 20px;
    width: 320px;
    height: 62px;
  }
`;

// ==============================================
// CONFIRMATION PAGE COMPONENTS
// ==============================================

export const TitleConfirmEmailAddress = styled(Title)`
  margin-bottom: 0;
  width: 100%;
  text-align: center;
  @media ${breakpoints.tablet} {
    width: 381px;
  }
`;

// ==============================================
// BUSINESS FORM & SCHEDULE FORM SPECIFIC COMPONENTS
// ==============================================

export const InnerBusinessFormBox = styled.div`
  margin: 0;
  width: 100%;
  @media ${breakpoints.laptopM} {
    margin: 0 21px;
  }
  ${InputLabel} p {
    margin-top: 15px;
  }
  .businessTypeCategoryDiv {
    margin: 0;
  }
  .businessType {
    margin-top: 25px;
  }
  .categorySelect {
    margin-top: 15px;
    width: 100%;
  }
  & .MuiOutlinedInput-root {
    & fieldset {
      border-color: transparent;
    }
  }
  .MuiPaper-root.MuiMenu-paper.MuiPopover-paper.MuiPaper-elevation8.MuiPaper-rounded
    > ul {
    border: solid 3px blue;
  }
`;

export const TextFieldWrapBusinessForm = styled.div`
  @media ${breakpoints.tablet} {
    width: 598px;
  }
  input::file-selector-button {
    background-color: ${colors.red};
    color: ${colors.white};
    border: none;
    height: 42px;
    width: 122px;
    border-radius: 4px;
    border: 1px solid #000;
    margin-top: -50%;
  }
  input[type='file'] {
    color: #bdbdbd;
  }
  .yourDeliveryAreaClassMiles {
    color: #000;
    font-size: 16px;
    font-weight: 400;
  }
`;
